import { useCallback } from 'react';
import uuid from 'uuid/v4';

import { getAGGClient } from '@confluence/graphql/entry-points/getAGGClient.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useContentType, usePageContentId } from '@confluence/page-context';

import { HighlightKeywordsQuery } from './gql/HighlightKeywordsQuery.agggraphql.ts';
import { generatePageARI, generateWorkspaceId } from './reading-aids-utils.ts';
import type {
	KeyPhraseCategory,
	HighlightKeywordsQueryType,
	HighlightKeywordsQueryVariables,
} from './gql/__types__/HighlightKeywordsQuery.ts';

type HighlightPhraseNodeType = {
	phrase: string;
	category: KeyPhraseCategory;
};

type QueryHighlightAcronymsType = {
	acronymRegex?: RegExp;
	acronyms: HighlightPhraseNodeType[];
};

const cachedAcronymRegex = {
	pageARI: '',
	regex: new RegExp('(?!)'),
};

const getAcronymRegex = (
	acronyms: HighlightPhraseNodeType[],
	pageARI: string = '',
	ignoreCase: boolean = false,
): RegExp => {
	if (pageARI === cachedAcronymRegex.pageARI) {
		return cachedAcronymRegex.regex;
	}
	let regex;
	try {
		regex = new RegExp(
			acronyms.map((node) => `\\b${node.phrase}\\b`).join('|'),
			ignoreCase ? 'gi' : 'g',
		);
	} catch (_e) {
		regex = new RegExp('(?!)');
	}
	cachedAcronymRegex.pageARI = pageARI;
	cachedAcronymRegex.regex = regex;
	return regex;
};

export const useQueryHighlightAcronyms = () => {
	const [contentId] = usePageContentId();
	const [contentType] = useContentType();
	const { cloudId, activationId } = useSessionData();
	const pageARI = contentId ? generatePageARI(contentId, cloudId, contentType) : undefined;
	const workspaceId = generateWorkspaceId(activationId, cloudId);

	const queryHighlightAcronyms = useCallback(
		async ({
			cacheKey = uuid(),
		}: { cacheKey?: string } = {}): Promise<QueryHighlightAcronymsType> => {
			if (!workspaceId || !pageARI) {
				return {
					acronyms: [],
				};
			}

			const client = getAGGClient();
			try {
				// eslint-disable-next-line graphql-relay-compat/no-direct-apollo-query-and-mutation-operations -- Read http://go/cfe-no-direct-apollo-query-and-mutation-operations
				const { data } = await client.query<
					HighlightKeywordsQueryType,
					HighlightKeywordsQueryVariables
				>({
					query: HighlightKeywordsQuery,
					fetchPolicy: 'network-only',
					errorPolicy: 'ignore',
					variables: {
						// comment from confluence/next/packages/content-appearance/src/contentAppearance.ts
						// This is dummy variable is used to force "network-only" requests to only be run as network calls.
						// A bug in Apollo sometimes makes this query return data from the cache even if the policy is "network-only"
						// See for more details: https://pug.jira-dev.com/wiki/spaces/~683304838/pages/5971970200/Apollo+Client+-+weird+refetch+behavior
						cacheKey,
						pageARI,
						workspaceId,
					},
				});

				const keyPhrases = data?.knowledgeDiscovery?.keyPhrases?.nodes
					? data.knowledgeDiscovery.keyPhrases.nodes.map((node) => ({
							phrase: node.keyPhrase,
							category: node.category,
						}))
					: [];

				// getAcronymRegex use pageARI to cache the regex for the page
				// but for live edit pages we should be careful with the cache
				// because the page content is changing constantly
				const pageARIWithCacheKey = `${pageARI}-${cacheKey}`;
				let acronymRegex: RegExp | undefined = undefined;
				if (keyPhrases.length) {
					acronymRegex = getAcronymRegex(keyPhrases, pageARIWithCacheKey, true);
				}

				return {
					acronyms: keyPhrases,
					acronymRegex,
				};
			} catch (err) {
				return {
					acronyms: [],
				};
			}
		},
		[pageARI, workspaceId],
	);

	return { queryHighlightAcronyms };
};
